import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import './MonitorBaas.css';

/**
 * MonitorBaas.js — MODELO BANKING (Possibilidade 1 — Stark Bank)
 * ---------------------------------------------------------------------------
 * O Banking não tem ledger de compras por cartão nem eventos de autorização
 * síncrona (aquilo era do Issuing). O que existe é o EXTRATO da conta
 * (/api/baas/account/statement). Então o monitor acompanha as MOVIMENTAÇÕES:
 * painel de últimas movimentações (auto-refresh) + extrato filtrável.
 *
 * ===========================================================================
 * 🔔 EVENTOS DE WEBHOOK (novo)
 * ===========================================================================
 * Painel de leitura dos eventos recebidos via /api/baas/webhook/stark/*,
 * gravados em baas_stark_webhook_events e processados pelo worker
 * (jobs/webhookEventsWorker.js). É best-effort, igual ao painel de compras:
 * falha ao carregar não derruba o resto do monitor.
 *
 * ===========================================================================
 * 🔴 AMBIENTE + POLLING
 * ===========================================================================
 * Esta tela recarrega sozinha a cada 20s e a versão anterior não revalidava
 * /account nesse ciclo: trocar o ambiente em outra aba deixava o monitor
 * exibindo movimentação de sandbox indefinidamente, como se fosse real.
 *
 * Agora todo ciclo confirma a conta. Se o escopo (ambiente::projectId) mudou,
 * feed, extrato e saldo são descartados e recarregados. E o ambiente fica
 * visível numa pill ao lado do status de tempo real.
 *
 * ===========================================================================
 * 🐛 BUGS CORRIGIDOS
 * ===========================================================================
 * 1) RESET DE FILTROS USAVA OS FILTROS ANTIGOS. O original fazia
 *    `setFilters(novos)` e `setTimeout(() => loadRows(null), 0)`. O timeout
 *    capturava o `loadRows` do render atual, cujo closure ainda tinha os
 *    filtros VELHOS — então "Limpar" buscava o período anterior. Agora
 *    `loadRows` recebe os filtros por parâmetro, sem depender de closure.
 *
 * 2) setState EM COMPONENTE DESMONTADO. Polling de 20s sem AbortController nem
 *    flag de vida: sair da tela deixava requisição em vôo escrevendo em estado
 *    morto. Corrigido em todas as cargas.
 *
 * 3) PERÍODO INVERTIDO PASSAVA BATIDO. "De" depois de "Até" devolvia lista
 *    vazia sem explicação — parecia que não havia movimentação. Agora valida
 *    antes de chamar a API.
 *
 * 4) "TEMPO REAL ATIVO" PROMETIA O QUE NÃO ENTREGA. O extrato da Stark chega
 *    por polling, não por socket. A pill agora fala de atualização automática,
 *    e o socket aparece com o nome do que ele é.
 * ---------------------------------------------------------------------------
 */

const fmt = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

const fmtTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleTimeString('pt-BR');
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleString('pt-BR');
};

/** Data ISO em horário LOCAL (não usar toISOString: desloca o dia em UTC-3). */
const isoLocal = (d) => {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const DAY_MS = 86_400_000;
const POLL_MS = 20_000;
const ROWS_PAGE = 25;
const ROWS_MAX = 500;          // teto acumulado na tela
const FEED_LIMIT = 15;
const PURCHASES_LIMIT = 30;    // últimas compras exibidas no painel
const WEBHOOK_EVENTS_LIMIT = 20; // últimos eventos de webhook exibidos no painel

const ENV_LABEL = { production: 'PRODUÇÃO', sandbox: 'SANDBOX' };
const envLabelOf = (env) => ENV_LABEL[env] || String(env || '').toUpperCase() || '—';

const asArray = (v) => (Array.isArray(v) ? v : []);
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * Classificação DEFENSIVA do status de uma compra.
 *
 * ⚠️ Ainda não temos um `_raw` real de compra do modelo Banking (produção sem
 * compras no momento da implementação). Então NÃO presumimos a lista fechada de
 * status da Stark. A regra é tolerante:
 *   - valores conhecidos de aprovação → "aprovada"
 *   - valores conhecidos de recusa/cancelamento → "recusada"
 *   - qualquer outro → "outro", exibindo o status cru como rótulo (nunca
 *     rotulamos como aprovada algo que não reconhecemos).
 * Quando a primeira compra real aparecer, basta conferir os status observados.
 */
const APROVADA = new Set(['approved', 'confirmed', 'accepted', 'settled', 'success']);
const RECUSADA = new Set(['denied', 'declined', 'canceled', 'cancelled', 'reversed', 'refunded', 'failed', 'voided']);

const classificarStatus = (statusRaw) => {
  const s = String(statusRaw || '').toLowerCase().trim();
  if (!s) return { grupo: 'outro', label: 'Sem status', cor: '#6b7280' };
  if (APROVADA.has(s)) return { grupo: 'aprovada', label: 'Aprovada', cor: '#10b981' };
  if (RECUSADA.has(s)) return { grupo: 'recusada', label: 'Recusada', cor: '#ef4444' };
  return { grupo: 'outro', label: statusRaw, cor: '#6b7280' };
};

/**
 * Procura, de forma tolerante, um motivo textual de recusa no objeto da compra.
 * Como não confirmamos que o modelo Banking traz esse campo, tentamos os nomes
 * mais prováveis e, se nenhum existir, simplesmente não exibimos motivo (sem
 * inventar). O dia em que a Stark trouxer, aparece sozinho.
 */
const motivoRecusa = (p) => {
  if (!p || typeof p !== 'object') return '';
  for (const chave of ['reason', 'declineReason', 'deniedReason', 'statusReason', 'description']) {
    const v = p[chave];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
};

/** Compras vêm em CENTAVOS da Stark (ver listCorporatePurchases). */
const centavosParaReais = (c) => (Number.isFinite(Number(c)) ? Number(c) / 100 : 0);

/**
 * Classificação do status de um evento de webhook (baas_stark_webhook_events).
 * Diferente das compras, aqui a lista de status É fechada (é a gente que
 * define no ENUM da tabela), então não precisa ser defensivo do mesmo jeito.
 */
const WEBHOOK_STATUS_INFO = {
  processed: { label: 'Processado', cor: '#10b981', icone: '✓' },
  error: { label: 'Erro', cor: '#ef4444', icone: '✕' },
  ignored: { label: 'Ignorado', cor: '#6b7280', icone: '•' },
  received: { label: 'Recebido (na fila)', cor: '#f59e0b', icone: '•' },
};
const classificarWebhookStatus = (status) =>
  WEBHOOK_STATUS_INFO[status] || { label: status || 'Desconhecido', cor: '#6b7280', icone: '•' };

/** `log_type` chega do banco como JSON cru (com aspas por fora, ex: '"paid"'). */
const limparLogType = (v) => {
  if (!v) return '';
  const s = String(v).trim();
  return s.replace(/^"|"$/g, '');
};

const scopeOf = (account) => (account
  ? `${account.environment || '?'}::${account.stark_project_id || '?'}`
  : null);

const apiErr = (e, fallback = 'Erro ao buscar o extrato.') => {
  if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError') return null;
  const status = e?.response?.status;
  if (status === 403) return 'Seu perfil não tem permissão para ver o extrato da conta.';
  if (status === 409) return 'A conta Stark Bank não está conectada.';
  return e?.response?.data?.error || fallback;
};

const filtrosPadrao = () => ({ after: isoLocal(new Date(Date.now() - 7 * DAY_MS)), before: '' });

export default function MonitorBaas({ connected: socketConnected }) {
  const [accountConnected, setAccountConnected] = useState(null); // null = carregando
  const [account, setAccount] = useState(null);
  const [balance, setBalance] = useState(null);

  const [feed, setFeed] = useState([]);

  // Compras nos cartões corporativos (CorporatePurchase). Painel de leitura.
  const [purchases, setPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(true);

  // Eventos de webhook (Stark → nosso backend). Painel de leitura, com
  // scroll infinito (sem botão "próximo" — rolar o mouse já carrega mais).
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [loadingWebhookEvents, setLoadingWebhookEvents] = useState(true);
  const [loadingMoreWebhookEvents, setLoadingMoreWebhookEvents] = useState(false);
  const [webhookCursor, setWebhookCursor] = useState(null); // null = não há mais páginas
  const webhookListRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loadingRows, setLoadingRows] = useState(true);

  const [filters, setFilters] = useState(filtrosPadrao);

  const aliveRef = useRef(true);
  const scopeRef = useRef(null);
  // Guarda os filtros efetivamente aplicados na última busca. É o que permite
  // paginar e recarregar sem depender do closure do render.
  const filtrosAplicadosRef = useRef(filtrosPadrao());

  useEffect(() => () => { aliveRef.current = false; }, []);

  const environment = account?.environment || null;
  const isProd = environment === 'production';

  const limparEscopo = useCallback(() => {
    setBalance(null);
    setFeed([]);
    setPurchases([]);
    setWebhookEvents([]);
    setWebhookCursor(null);
    setRows([]);
    setCursor(null);
  }, []);

  const loadBalance = useCallback(async (signal) => {
    try {
      const { data } = await api.get('/api/baas/account/balance', { signal });
      if (aliveRef.current) setBalance(data);
    } catch {
      // pill de saldo é informativa — falha não interrompe o monitor
    }
  }, []);

  const loadFeed = useCallback(async (signal) => {
    try {
      const { data } = await api.get('/api/baas/account/statement', {
        params: { limit: FEED_LIMIT },
        signal,
      });
      if (aliveRef.current) setFeed(asArray(data?.transactions));
    } catch {
      // silencioso: o extrato abaixo continua sendo a fonte confiável
    }
  }, []);

  /**
   * Carrega as últimas compras nos cartões corporativos. Best-effort, igual ao
   * feed: se a Stark não devolver nada (ou o produto não expuser compras), o
   * painel apenas mostra o estado vazio, sem derrubar o resto do monitor.
   */
  const loadPurchases = useCallback(async (signal) => {
    setLoadingPurchases(true);
    try {
      const { data } = await api.get('/api/baas/account/purchases', {
        params: { limit: PURCHASES_LIMIT },
        signal,
      });
      if (aliveRef.current) setPurchases(asArray(data?.purchases));
    } catch {
      // Painel informativo — falha não interrompe o monitor. O extrato segue
      // sendo a fonte confiável de movimentação da conta.
    } finally {
      if (aliveRef.current) setLoadingPurchases(false);
    }
  }, []);

  /**
   * Carrega a PRIMEIRA página de eventos de webhook (substitui a lista
   * inteira). Usada na carga inicial e na troca de ambiente — mesmo espírito
   * de loadPurchases: best-effort, painel informativo.
   */
  const loadWebhookEvents = useCallback(async (signal) => {
    setLoadingWebhookEvents(true);
    try {
      const { data } = await api.get('/api/baas/account/webhook-events', {
        params: { limit: WEBHOOK_EVENTS_LIMIT },
        signal,
      });
      if (!aliveRef.current) return;
      setWebhookEvents(asArray(data?.events));
      setWebhookCursor(data?.next_cursor || null);
    } catch {
      // Painel informativo — falha não interrompe o monitor.
    } finally {
      if (aliveRef.current) setLoadingWebhookEvents(false);
    }
  }, []);

  /**
   * Ciclo de POLLING (a cada 20s): busca a primeira página de novo, mas só
   * ENCAIXA no topo os eventos realmente novos (id maior que o mais recente já
   * na tela). Não mexe no cursor nem na lista já carregada por scroll — senão
   * o usuário perderia a posição/o histórico toda vez que a tela atualiza
   * sozinha, que era exatamente o comportamento chato que queríamos evitar.
   */
  const refreshWebhookEventsTop = useCallback(async (signal) => {
    try {
      const { data } = await api.get('/api/baas/account/webhook-events', {
        params: { limit: WEBHOOK_EVENTS_LIMIT },
        signal,
      });
      if (!aliveRef.current) return;
      const fresh = asArray(data?.events);
      setWebhookEvents((prev) => {
        if (prev.length === 0) return fresh; // nada carregado ainda: usa a página cheia
        const maxIdAtual = prev[0]?.id || 0;
        const novos = fresh.filter((e) => Number(e?.id) > maxIdAtual);
        return novos.length ? [...novos, ...prev] : prev;
      });
    } catch {
      // silencioso: é só o refresh automático de fundo
    }
  }, []);

  /**
   * Scroll infinito de verdade: busca a PRÓXIMA página (mais antiga) a partir
   * do cursor atual e ENCAIXA no fim da lista. Sem botão "carregar mais" —
   * quem dispara isso é o handler de scroll lá embaixo.
   */
  const loadMoreWebhookEvents = useCallback(async () => {
    if (!webhookCursor || loadingMoreWebhookEvents) return;
    setLoadingMoreWebhookEvents(true);
    try {
      const { data } = await api.get('/api/baas/account/webhook-events', {
        params: { limit: WEBHOOK_EVENTS_LIMIT, before_id: webhookCursor },
      });
      if (!aliveRef.current) return;
      setWebhookEvents((prev) => [...prev, ...asArray(data?.events)]);
      setWebhookCursor(data?.next_cursor || null);
    } catch {
      // silencioso: o usuário pode tentar rolar de novo
    } finally {
      if (aliveRef.current) setLoadingMoreWebhookEvents(false);
    }
  }, [webhookCursor, loadingMoreWebhookEvents]);

  /**
   * Detecta "chegou perto do fim da caixa" e dispara a próxima página.
   * Threshold de 60px de folga — carrega um pouco ANTES de bater o fundo de
   * verdade, pra não deixar o usuário esperando o próprio scroll travar.
   */
  const handleWebhookScroll = useCallback(() => {
    const el = webhookListRef.current;
    if (!el) return;
    const pertoDoFim = el.scrollTop + el.clientHeight >= el.scrollHeight - 60;
    if (pertoDoFim) loadMoreWebhookEvents();
  }, [loadMoreWebhookEvents]);

  /**
   * Carrega o extrato.
   *
   * ⚠️ `filtrosArg` vem por PARÂMETRO de propósito. Depender de `filters` do
   * closure é o que fazia o botão "Limpar" buscar com o período antigo.
   */
  const loadRows = useCallback(async (nextCursor = null, filtrosArg = null, signal = undefined) => {
    const f = filtrosArg || filtrosAplicadosRef.current;

    if (f.after && f.before && f.after > f.before) {
      toast.error('O período está invertido: a data inicial é depois da final.');
      return;
    }

    filtrosAplicadosRef.current = f;
    setLoadingRows(true);
    try {
      const params = { limit: ROWS_PAGE };
      if (f.after) params.after = f.after;
      if (f.before) params.before = f.before;
      if (nextCursor) params.cursor = nextCursor;

      const { data } = await api.get('/api/baas/account/statement', { params, signal });
      if (!aliveRef.current) return;

      const lote = asArray(data?.transactions);
      setRows((prev) => (nextCursor ? [...prev, ...lote].slice(0, ROWS_MAX) : lote));
      setCursor(data?.cursor || null);
    } catch (err) {
      const msg = apiErr(err);
      if (msg && aliveRef.current) toast.error(msg);
    } finally {
      if (aliveRef.current) setLoadingRows(false);
    }
  }, []);

  /**
   * Confirma a conta e o ambiente. `silent` é o ciclo de polling — ele revalida
   * o ambiente do mesmo jeito, que é justamente o que faltava antes.
   */
  const syncAccount = useCallback(async ({ silent = false, signal } = {}) => {
    try {
      const { data } = await api.get('/api/baas/account', { signal });
      if (!aliveRef.current) return false;

      const nextAccount = data?.account || null;
      const isConnected = !!data?.connected;
      const nextScope = scopeOf(nextAccount);
      const trocou = !!scopeRef.current && scopeRef.current !== nextScope;

      scopeRef.current = nextScope;
      setAccount(nextAccount);
      setAccountConnected(isConnected);

      if (trocou) {
        // Ambiente novo: nada do anterior serve, e os filtros voltam ao padrão
        // porque período de outro ambiente não tem significado aqui.
        limparEscopo();
        const novos = filtrosPadrao();
        setFilters(novos);
        filtrosAplicadosRef.current = novos;
        if (isConnected) {
          loadBalance(signal);
          loadFeed(signal);
          loadPurchases(signal);
          loadWebhookEvents(signal);
          loadRows(null, novos, signal);
        }
        if (!silent) toast.info(`Ambiente alterado para ${envLabelOf(nextAccount?.environment)}. Dados recarregados.`);
        return isConnected;
      }

      if (!isConnected) limparEscopo();
      return isConnected;
    } catch (e) {
      if (!aliveRef.current) return false;
      const aborted = e?.name === 'CanceledError' || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED';
      if (!aborted) setAccountConnected(false);
      return false;
    }
  }, [limparEscopo, loadBalance, loadFeed, loadPurchases, loadWebhookEvents, loadRows]);

  // ── Carga inicial ────────────────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const ok = await syncAccount({ signal: controller.signal });
      if (!aliveRef.current) return;
      if (ok) {
        loadBalance(controller.signal);
        loadFeed(controller.signal);
        loadPurchases(controller.signal);
        loadWebhookEvents(controller.signal);
        loadRows(null, filtrosAplicadosRef.current, controller.signal);
      } else {
        setLoadingRows(false);
      }
    })();
    return () => controller.abort();
  }, [syncAccount, loadBalance, loadFeed, loadPurchases, loadWebhookEvents, loadRows]);

  // ── Polling: revalida a CONTA junto do feed e do saldo ───────────────────
  useEffect(() => {
    if (accountConnected !== true) return undefined;
    const controller = new AbortController();
    const t = setInterval(async () => {
      const ok = await syncAccount({ silent: false, signal: controller.signal });
      if (!ok || !aliveRef.current) return;
      loadFeed(controller.signal);
      loadBalance(controller.signal);
      loadPurchases(controller.signal);
      refreshWebhookEventsTop(controller.signal);
    }, POLL_MS);
    return () => { clearInterval(t); controller.abort(); };
  }, [accountConnected, syncAccount, loadFeed, loadBalance, loadPurchases, refreshWebhookEventsTop]);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    loadRows(null, filters);
  };

  const handleResetFilters = () => {
    const novos = filtrosPadrao();
    setFilters(novos);
    // Passa os novos filtros direto — sem setTimeout, sem closure velho.
    loadRows(null, novos);
  };

  const totalRows = rows.length;
  const noTeto = totalRows >= ROWS_MAX;

  const resumo = useMemo(() => {
    let entradas = 0; let saidas = 0;
    for (const t of rows) {
      const v = num(t?.amount);
      if (v >= 0) entradas += v; else saidas += Math.abs(v);
    }
    return { entradas, saidas };
  }, [rows]);

  // Resumo das compras por classificação (defensivo — ver classificarStatus).
  const resumoCompras = useMemo(() => {
    let aprovadas = 0; let recusadas = 0; let outras = 0;
    for (const p of purchases) {
      const g = classificarStatus(p?.status).grupo;
      if (g === 'aprovada') aprovadas += 1;
      else if (g === 'recusada') recusadas += 1;
      else outras += 1;
    }
    return { aprovadas, recusadas, outras, total: purchases.length };
  }, [purchases]);

  // Resumo dos eventos de webhook por status.
  const resumoWebhookEvents = useMemo(() => {
    let processados = 0; let erros = 0; let pendentes = 0;
    for (const ev of webhookEvents) {
      if (ev?.status === 'processed') processados += 1;
      else if (ev?.status === 'error') erros += 1;
      else if (ev?.status === 'received') pendentes += 1;
    }
    return { processados, erros, pendentes, total: webhookEvents.length };
  }, [webhookEvents]);

  if (accountConnected === null) {
    return (
      <div className="mb-page">
        <div className="mb-empty">
          <p>Confirmando ambiente da conta...</p>
        </div>
      </div>
    );
  }

  if (accountConnected === false) {
    return (
      <div className="mb-page">
        <div className="mb-empty">
          <span className="mb-empty-icon">🔌</span>
          <p>Nenhuma conta Stark Bank conectada.</p>
          <small>Conecte a conta na tela "Conta Digital Stark Bank" para acompanhar as movimentações.</small>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-page">
      {/* ── HEADER / STATUS ────────────────────────────────────────────── */}
      <div className="mb-header">
        <div>
          <h2>
            Monitor da Conta
            {environment && (
              <span className={`mb-env-badge ${isProd ? 'is-prod' : 'is-sbx'}`}>
                ● {envLabelOf(environment)}
              </span>
            )}
          </h2>
          <p>Acompanhe as movimentações da conta Stark Bank conforme entram no extrato.</p>
        </div>

        <div className="mb-status-pills">
          {/* O extrato da Stark chega por POLLING. Chamar isso de "tempo real"
              prometia o que a integração não entrega. */}
          <span className="mb-pill mb-pill--ok">
            <span className="mb-pill-dot" />
            Atualiza a cada {POLL_MS / 1000}s
          </span>
          <span className={`mb-pill ${socketConnected ? 'mb-pill--ok' : 'mb-pill--off'}`}>
            <span className="mb-pill-dot" />
            {socketConnected ? 'Socket conectado' : 'Socket offline'}
          </span>
          {balance && (
            <span className="mb-pill mb-pill--ok">
              <span className="mb-pill-dot" />
              Saldo: {fmt(balance.amount)}
            </span>
          )}
        </div>
      </div>

      {!isProd && (
        <div className="mb-env-alert">
          <strong>Movimentações do ambiente de TESTES (Sandbox).</strong>
          <span> Nada aqui representa dinheiro real. Não use para conciliação nem relatório.</span>
        </div>
      )}

      <div className="mb-layout">
        {/* ── ÚLTIMAS MOVIMENTAÇÕES ────────────────────────────────────── */}
        <section className="mb-panel mb-feed-panel">
          <div className="mb-panel-header">
            <h3>Últimas movimentações</h3>
            <span className="mb-feed-count">atualiza a cada {POLL_MS / 1000}s</span>
          </div>

          <div className="mb-feed-list">
            {feed.length === 0 ? (
              <div className="mb-empty">
                <span className="mb-empty-icon">📡</span>
                <p>Nenhuma movimentação recente.</p>
                <small>Entradas e saídas da conta aparecem aqui automaticamente.</small>
              </div>
            ) : (
              feed.map((ev, i) => {
                const v = num(ev?.amount);
                const isIn = v >= 0;
                const color = isIn ? '#10b981' : '#ef4444';
                const bg = isIn ? 'rgba(16,185,129,.10)' : 'rgba(239,68,68,.10)';
                return (
                  <div key={ev?.id || `ev-${i}`} className="mb-feed-item">
                    <div className="mb-feed-icon" style={{ background: bg, color }}>
                      {isIn ? '⬆️' : '⬇️'}
                    </div>
                    <div className="mb-feed-info">
                      <div className="mb-feed-top">
                        <span className="mb-feed-label" style={{ color }}>{isIn ? 'Entrada' : 'Saída'}</span>
                        <span className="mb-feed-time">{fmtTime(ev?.created)}</span>
                      </div>
                      <p>{ev?.description || ev?.source || 'Movimentação'}</p>
                    </div>
                    <div className="mb-feed-value" style={{ color }}>
                      {isIn ? '+' : '−'} {fmt(Math.abs(v))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* ── COMPRAS NOS CARTÕES (CorporatePurchase) ──────────────────── */}
        <section className="mb-panel mb-purchases-panel">
          <div className="mb-panel-header">
            <h3>Compras nos cartões</h3>
            <span className="mb-feed-count">atualiza a cada {POLL_MS / 1000}s</span>
          </div>

          {resumoCompras.total > 0 && (
            <div className="mb-compras-resumo">
              <span className="mb-chip mb-chip--ok">{resumoCompras.aprovadas} aprovada{resumoCompras.aprovadas === 1 ? '' : 's'}</span>
              <span className="mb-chip mb-chip--bad">{resumoCompras.recusadas} recusada{resumoCompras.recusadas === 1 ? '' : 's'}</span>
              {resumoCompras.outras > 0 && (
                <span className="mb-chip mb-chip--neutro">{resumoCompras.outras} outro{resumoCompras.outras === 1 ? '' : 's'}</span>
              )}
            </div>
          )}

          <div className="mb-compras-list">
            {loadingPurchases && purchases.length === 0 ? (
              <div className="mb-empty"><p>Carregando compras...</p></div>
            ) : purchases.length === 0 ? (
              <div className="mb-empty">
                <span className="mb-empty-icon">💳</span>
                <p>Nenhuma compra registrada.</p>
                <small>
                  As compras feitas nos cartões corporativos aparecem aqui — aprovadas e recusadas.
                  Cartões físicos só passam a registrar depois de desbloqueados.
                </small>
              </div>
            ) : (
              purchases.map((p, i) => {
                const st = classificarStatus(p?.status);
                const valor = centavosParaReais(p?.amount);
                const motivo = st.grupo === 'recusada' ? motivoRecusa(p) : '';
                const estabelecimento = p?.merchantDisplayName || p?.merchantName || 'Estabelecimento não informado';
                return (
                  <div key={p?.id || `pur-${i}`} className="mb-compra-item">
                    <div className="mb-compra-icon" style={{ color: st.cor, background: `${st.cor}1a` }}>
                      {st.grupo === 'aprovada' ? '✓' : st.grupo === 'recusada' ? '✕' : '•'}
                    </div>
                    <div className="mb-compra-info">
                      <div className="mb-compra-top">
                        <span className="mb-compra-status" style={{ color: st.cor }}>{st.label}</span>
                        <span className="mb-compra-time">{fmtDateTime(p?.created)}</span>
                      </div>
                      <p className="mb-compra-merchant">{estabelecimento}</p>
                      <div className="mb-compra-meta">
                        {p?.cardEnding && <span>cartão •••• {p.cardEnding}</span>}
                        {p?.holderName && <span>{p.holderName}</span>}
                        {p?.methodCode && <span>{p.methodCode}</span>}
                      </div>
                      {/* Motivo só aparece se a Stark tiver enviado — nunca inventamos. */}
                      {motivo && <p className="mb-compra-motivo">Motivo: {motivo}</p>}
                    </div>
                    <div className="mb-compra-valor" style={{ color: st.cor }}>
                      {fmt(valor)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* ── EVENTOS DE WEBHOOK (Stark → seu sistema) ────────────────────── */}
        <section className="mb-panel mb-webhook-panel">
          <div className="mb-panel-header">
            <h3>Eventos recebidos (webhook)</h3>
            <span className="mb-feed-count">atualiza a cada {POLL_MS / 1000}s</span>
          </div>

          {resumoWebhookEvents.total > 0 && (
            <div className="mb-compras-resumo">
              <span className="mb-chip mb-chip--ok">{resumoWebhookEvents.processados} processado{resumoWebhookEvents.processados === 1 ? '' : 's'}</span>
              {resumoWebhookEvents.erros > 0 && (
                <span className="mb-chip mb-chip--bad">{resumoWebhookEvents.erros} erro{resumoWebhookEvents.erros === 1 ? '' : 's'}</span>
              )}
              {resumoWebhookEvents.pendentes > 0 && (
                <span className="mb-chip mb-chip--neutro">{resumoWebhookEvents.pendentes} na fila</span>
              )}
            </div>
          )}

          <div className="mb-compras-list">
            {loadingWebhookEvents && webhookEvents.length === 0 ? (
              <div className="mb-empty"><p>Carregando eventos...</p></div>
            ) : webhookEvents.length === 0 ? (
              <div className="mb-empty">
                <span className="mb-empty-icon">🔔</span>
                <p>Nenhum evento recebido ainda.</p>
                <small>Eventos de transferência, cobrança e boleto aparecem aqui assim que a Stark Bank notifica.</small>
              </div>
            ) : (
              webhookEvents.map((ev) => {
                const st = classificarWebhookStatus(ev?.status);
                const logType = limparLogType(ev?.log_type);
                return (
                  <div key={ev?.id} className="mb-compra-item">
                    <div className="mb-compra-icon" style={{ color: st.cor, background: `${st.cor}1a` }}>
                      {st.icone}
                    </div>
                    <div className="mb-compra-info">
                      <div className="mb-compra-top">
                        <span className="mb-compra-status" style={{ color: st.cor }}>{st.label}</span>
                        <span className="mb-compra-time">{fmtDateTime(ev?.received_at)}</span>
                      </div>
                      <p className="mb-compra-merchant">
                        {ev?.subscription || '—'}{logType ? ` · ${logType}` : ''}
                      </p>
                      {/* Erro só aparece se o worker tiver registrado um — nunca inventamos. */}
                      {ev?.error_message && <p className="mb-compra-motivo">Erro: {ev.error_message}</p>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* ── EXTRATO / FILTROS ────────────────────────────────────────── */}
        <section className="mb-panel mb-history-panel">
          <div className="mb-panel-header">
            <h3>Extrato</h3>
            <button
              className="mb-btn-refresh"
              onClick={() => loadRows(null, filtrosAplicadosRef.current)}
              disabled={loadingRows}
            >
              {loadingRows ? 'Atualizando...' : '⟳ Atualizar'}
            </button>
          </div>

          <form className="mb-filters" onSubmit={handleApplyFilters}>
            <div className="mb-filter-field">
              <label>De</label>
              <input
                type="date"
                value={filters.after}
                max={filters.before || isoLocal(new Date())}
                onChange={(e) => setFilters((p) => ({ ...p, after: e.target.value }))}
              />
            </div>
            <div className="mb-filter-field">
              <label>Até</label>
              <input
                type="date"
                value={filters.before}
                min={filters.after || undefined}
                onChange={(e) => setFilters((p) => ({ ...p, before: e.target.value }))}
              />
            </div>
            <div className="mb-filter-actions">
              <button type="button" className="mb-btn-ghost" onClick={handleResetFilters} disabled={loadingRows}>Limpar</button>
              <button type="submit" className="mb-btn-primary" disabled={loadingRows}>Filtrar</button>
            </div>
          </form>

          {totalRows > 0 && (
            <div className="mb-resumo">
              <span>Entradas <strong className="mb-txt-green">{fmt(resumo.entradas)}</strong></span>
              <span>Saídas <strong className="mb-txt-red">{fmt(resumo.saidas)}</strong></span>
              <span>{totalRows} linha{totalRows === 1 ? '' : 's'}{noTeto ? ' (limite da tela)' : ''}</span>
            </div>
          )}

          <div className="mb-table-wrap">
            {loadingRows && totalRows === 0 ? (
              <div className="mb-empty"><p>Carregando extrato...</p></div>
            ) : totalRows === 0 ? (
              <div className="mb-empty">
                <span className="mb-empty-icon">🔍</span>
                <p>Nenhuma movimentação no período selecionado.</p>
              </div>
            ) : (
              <table className="mb-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Descrição</th>
                    <th>Origem</th>
                    <th className="mb-th-right">Saldo</th>
                    <th className="mb-th-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t, i) => {
                    const v = num(t?.amount);
                    return (
                      <tr key={t?.id || `row-${i}`}>
                        <td className="mb-td-nowrap">{fmtDateTime(t?.created)}</td>
                        <td className="mb-cell-desc">{t?.description || '—'}</td>
                        <td className="mb-td-nowrap">{t?.source || '—'}</td>
                        <td className="mb-td-right">{fmt(t?.balance)}</td>
                        <td className={`mb-td-right ${v >= 0 ? 'mb-txt-green' : 'mb-txt-red'}`}>
                          {v >= 0 ? '+' : '−'} {fmt(Math.abs(v))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {cursor && !noTeto && (
            <div className="mb-pagination">
              <button
                className="mb-btn-ghost"
                onClick={() => loadRows(cursor, filtrosAplicadosRef.current)}
                disabled={loadingRows}
              >
                {loadingRows ? 'Carregando...' : 'Carregar mais'}
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}